import { useEffect, useRef, useState } from "react";
import { API, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SaveToBoardChip } from "@/components/SaveToBoardChip";
import { PaperPlaneRight, Compass, Sparkle, BookmarkSimple } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Plan a 7-day trip to Japan in spring",
  "Best rooftop bars & nightlife in Barcelona",
  "Top restaurants and street-food markets in Bangkok",
  "Cozy cafes and social spots in Lisbon",
  "Where can I hike for a week under $1500?",
  "Local dining and live-music venues in Marrakech",
];

function renderInline(text) {
  // Parse **bold** segments into <strong>; leave the rest as plain text.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i} className="font-semibold text-[#1A1C19]">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

function renderContent(text) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      return <hr key={i} className="my-3 border-t border-[#EAEBE6]" />;
    }
    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const cls = level <= 1 ? "text-lg font-bold mt-1" : level === 2 ? "text-base font-bold mt-1" : "text-sm font-bold";
      return (
        <p key={i} className={`font-display text-[#1A1C19] ${cls}`}>
          {renderInline(heading[2])}
        </p>
      );
    }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      return (
        <div key={i} className="flex gap-2 pl-1">
          <span className="text-[#E8B291] mt-1.5 h-1.5 w-1.5 rounded-full bg-[#E8B291] shrink-0" />
          <span>{renderInline(trimmed.slice(2))}</span>
        </div>
      );
    }
    if (!trimmed) return <div key={i} className="h-2" />;
    return <p key={i}>{renderInline(line)}</p>;
  });
}

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [listings, setListings] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Load listings once on mount for board-handoff chip matching.
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const mentionedListings = (text) => {
    if (!text) return [];
    const lower = text.toLowerCase();
    return listings.filter((l) => {
      const t = l.title.toLowerCase();
      const loc = l.location.toLowerCase();
      const country = (l.country || "").toLowerCase();
      return lower.includes(t) || lower.includes(loc) || (country && lower.includes(country));
    });
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const json = JSON.parse(line);
            if (json.session_id && !sessionId) setSessionId(json.session_id);
            if (json.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  role: "assistant",
                  content: copy[copy.length - 1].content + json.delta,
                };
                return copy;
              });
            }
            if (json.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: json.error };
                return copy;
              });
            }
          } catch {
            /* ignore non-JSON SSE keep-alive lines */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen flex flex-col bg-[#F4F4EF]">
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto py-8 space-y-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center pt-10 pb-6 animate-fade-up">
              <div className="h-16 w-16 rounded-2xl bg-[#2E523A] flex items-center justify-center mx-auto mb-5">
                <Compass size={32} weight="duotone" className="text-[#E8B291]" />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold">Meet Atlas</h1>
              <p className="text-[#63675E] mt-3 max-w-md mx-auto">
                Your personal trip concierge. Ask about destinations, itineraries, restaurants,
                bars and social spots — I'll craft the journey.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="p-4 rounded-2xl border border-[#D2D5CD] bg-white text-sm font-medium hover:border-[#2E523A]/50 hover:bg-white transition-colors duration-200 flex items-center gap-3"
                    data-testid={`suggestion-${s.slice(0, 10).replace(/\s/g, "-")}`}
                  >
                    <Sparkle size={18} weight="duotone" className="text-[#E8B291] shrink-0" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-fade-up`}>
              {m.role === "assistant" && (
                <div className="h-9 w-9 rounded-xl bg-[#2E523A] flex items-center justify-center mr-3 shrink-0 mt-1">
                  <Compass size={20} weight="duotone" className="text-[#E8B291]" />
                </div>
              )}
              <div
                className={`max-w-[80%] flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`px-5 py-4 rounded-2xl leading-relaxed text-[15px] ${
                    m.role === "user"
                      ? "bg-[#2E523A] text-white rounded-br-md"
                      : "bg-white border border-[#D2D5CD] text-[#1A1C19] rounded-bl-md"
                  }`}
                  data-testid={`chat-message-${m.role}`}
                >
                  {m.content ? (
                    <div className="space-y-1">{renderContent(m.content)}</div>
                  ) : (
                    <span className="inline-flex gap-1" data-testid="chat-typing">
                      <span className="h-2 w-2 rounded-full bg-[#63675E] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#63675E] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#63675E] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  )}
                </div>
                {m.role === "assistant" && !sending && m.content && mentionedListings(m.content).length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid={`chat-handoff-${i}`}>
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#4A6E7C] w-full">
                      <BookmarkSimple size={14} weight="duotone" /> Add to a trip board
                    </span>
                    {mentionedListings(m.content).map((l) => (
                      <SaveToBoardChip key={l.id} listing={l} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 pb-6 pt-2 bg-[#F4F4EF]">
          <div className="flex items-end gap-2 bg-white border border-[#D2D5CD] rounded-3xl p-2 shadow-sm focus-within:border-[#2E523A]/50 transition-colors duration-200">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Describe your dream trip…"
              className="flex-1 resize-none bg-transparent px-4 py-2.5 outline-none text-[15px] max-h-32"
              data-testid="ai-chat-input"
            />
            <Button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="rounded-full h-11 w-11 p-0 bg-[#2E523A] hover:bg-[#26442f] shrink-0"
              data-testid="chat-send-btn"
            >
              <PaperPlaneRight size={20} weight="fill" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
